import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Warning, SignIn, Eye, EyeSlash } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Tüm alanları doldurun'); return; }
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Giriş başarılı');
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'officer') navigate('/officer');
      else navigate('/reporter');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Giriş başarısız');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1759701058584-ea4bdce848c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxlbWVyZ2VuY3klMjBsaWdodHMlMjBibHVyfGVufDB8fHx8MTc3NDExODk2MHww&ixlib=rb-4.1.0&q=85')" }}
      />
      <div className="absolute inset-0 bg-[#0A0A0A]/80" />

      <div className="relative z-10 w-full max-w-md animate-slide-up">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <Warning weight="fill" className="w-8 h-8 text-[hsl(var(--primary))]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight">ACİL İHBAR</h1>
          <p className="overline text-[hsl(var(--muted-foreground))] mt-2">Vatandaş İhbar Sistemi</p>
        </div>

        <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-white font-heading">GİRİŞ YAP</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">E-Posta</label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="h-14 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666]"
                />
              </div>
              <div>
                <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Sifre</label>
                <div className="relative">
                  <Input
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Şifrenizi girin"
                    className="h-14 bg-[#0A0A0A] border-[#2C2C2E] text-white placeholder:text-[#666] pr-12"
                  />
                  <button type="button" data-testid="login-toggle-password" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white transition-colors">
                    {showPassword ? <EyeSlash weight="bold" className="w-5 h-5" /> : <Eye weight="bold" className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-heading text-lg uppercase tracking-wide"
              >
                <SignIn weight="bold" className="w-5 h-5 mr-2" />
                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/register" data-testid="register-link" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-white transition-colors">
                Hesabınız yok mu? <span className="text-[hsl(var(--primary))] font-semibold">Kayıt Ol</span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
